import api from '../../../../api';
import { Medialink } from '../../../../api/medialink';
import { NodeWithoutDescription } from '../../../../api/table-of-content';

export interface SearchResultBase {
  id: string;
  kind: string;
  name: string;
  moduleId: string;
  group: string;
  owner: string;
  shared?: boolean;
  isAssignment?: boolean;
  metadata?: Record<string, unknown>;
}

export interface SearchResultMedialink extends SearchResultBase {
  docType: 'medialink';
  content?: string;
  hierarchy: NodeWithoutDescription[];
  contentType: Medialink['contentType'];
  inReview: boolean;
}

export interface SearchResultDigibookPage extends SearchResultBase {
  docType: 'digibook_page';
  pageNumber: number;
  hierarchy?: NodeWithoutDescription[];
  metadata: {
    digibookName: string;
    digibookId: string;
  };
}

export interface SearchResultManualPage
  extends Omit<SearchResultBase, 'kind' | 'group' | 'owner' | 'shared' | 'isAssignment'> {
  docType: 'margin_manual_page';
  pageNumber: number;
  hierarchy: NodeWithoutDescription[];
  metadata: {
    score: number;
    digibookId: string;
    digibookName: string;
  };
}

export interface SearchResultPopupManualPage
  extends Omit<SearchResultBase, 'kind' | 'group' | 'owner' | 'shared' | 'isAssignment'> {
  docType: 'popup_manual_page';
  pageNumber: number;
  popupManualPageNumber: number;
  hierarchy: NodeWithoutDescription[];
  metadata: {
    score: number;
    digibookId: string;
    digibookName: string;
  };
}

export interface SearchResultTextAnnotation
  extends Omit<SearchResultBase, 'kind' | 'group' | 'owner' | 'shared' | 'isAssignment'> {
  docType: 'text_annotation';
  pageNumber: number;
  hierarchy: NodeWithoutDescription[];
  metadata: {
    score: number;
    digibookId: string;
    digibookName: string;
    annotationSetName?: string;
    annotationSetId?: number;
  };
}

export interface SearchResultUserMaterial extends SearchResultBase {
  docType: 'usermaterial';
  content?: string;
  href?: string;
  hierarchy: NodeWithoutDescription[];
}

export interface SearchResultToc extends Omit<SearchResultBase, 'kind' | 'shared' | 'isAssignment'> {
  docType: 'toc';
  pathTitles: string[];
}

export interface SearchResultSlideSet extends Omit<SearchResultBase, 'kind'> {
  docType: 'slideset';
  hierarchy: NodeWithoutDescription[];
  state: string;
  version: number;
}

export interface SearchResultSlide extends Omit<SearchResultBase, 'kind'> {
  docType: 'slide';
  hierarchy: NodeWithoutDescription[];
  content: string;
  slideSetVersionSlideGroupId: string;
  slideId: string;
  state: string;
  version: number;
}

export type SearchResultItem =
  | SearchResultMedialink
  | SearchResultToc
  | SearchResultUserMaterial
  | SearchResultSlideSet
  | SearchResultSlide
  | SearchResultDigibookPage
  | SearchResultManualPage
  | SearchResultPopupManualPage
  | SearchResultTextAnnotation;

export type SearchTab = 'all' | 'material' | 'toc' | 'slideset' | 'digibook';

export interface SearchFacets {
  all: number;
  material: number;
  toc: number;
  slideset: number;
  digibook: number;
}

export interface FacetsResponse {
  data: SearchFacets;
}

export interface SearchResponse {
  data: SearchResultItem[];
  nextOffset: number | null;
  offset: number;
  queryTextEffective: string;
  resultsTotal: number;
  queryMs: number;
}

export async function searchFacets(moduleId: string, term: string): Promise<SearchFacets> {
  const params = new URLSearchParams({ q: term });
  const { data } = await api.get<FacetsResponse>(`/studio/modules/${moduleId}/medialinks/search/facets?${params}`);
  return data.data;
}

export async function search(
  moduleId: string,
  term: string,
  offset?: number,
  filter?: Exclude<SearchTab, 'all'>,
): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: term });
  if (offset) params.set('offset', JSON.stringify(offset));
  if (filter) params.set('filter', filter);
  const { data } = await api.get<SearchResponse>(`/studio/modules/${moduleId}/medialinks/search?${params}`);

  return data;
}
